<script setup lang="ts">
import type {JournalEntryDTO} from '@/ts/types/dto/journals-dto';
import FeelingChip from '@/vue/molecules/feeling-chip.vue';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import FormattedDate from '@/vue/atoms/formatted-date.vue';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import MoodFace from '@/vue/atoms/mood-face.vue';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {useMoodLevel} from '@/ts/composables/pure/use-mood-level';

const props = defineProps<{
  entry: JournalEntryDTO | null;
}>();

const isOpen = defineModel<boolean>({default: false});

const {t} = useI18n();
const {getMoodLevelLabel, getMoodLevelImage} = useMoodLevel();

const hasMoodLevel = computed(() => Boolean(props.entry?.moodLevel));

const moodLevelLabel = computed(() => {
  if (!props.entry?.moodLevel) {
    return '';
  }
  return getMoodLevelLabel(props.entry.moodLevel);
});

/**
 * Entries on the 10-mood vocabulary. Legacy entries keep their own smiley below rather than being
 * mapped onto a new mood — on this tab an old entry stays as it was written (IPD-3107).
 */
const hasMood = computed(() => Boolean(props.entry?.mood));

const moodName = computed(() => (props.entry?.mood ? t(`mood_${props.entry.mood}`) : ''));

const title = computed(() => {
  if (hasMood.value) {
    return moodName.value;
  }
  if (hasMoodLevel.value) {
    return t('feeling_level', {level: moodLevelLabel.value});
  }
  return props.entry?.name ?? '';
});

const allFeelings = computed(() => {
  const feelings: string[] = [];
  if (props.entry?.primaryFeelings) {
    feelings.push(...props.entry.primaryFeelings.map(f => f.name));
  }
  if (props.entry?.secondaryFeelings) {
    feelings.push(...props.entry.secondaryFeelings.map(f => f.name));
  }
  return feelings;
});

const hasFeelings = computed(() => allFeelings.value.length > 0);
const hasSideColumn = computed(() => Boolean(props.entry?.interventionName) || hasFeelings.value);

/**
 * Whether there is written text to show.
 *
 * The prompt is a label for the answer, so with no answer it labels nothing: a mood-only entry
 * showed "What's on your mind?" over empty space, reading as text that failed to load rather than
 * as an entry that never had any.
 */
const hasAnswer = computed(() => Boolean(props.entry?.answer));
</script>

<template>
  <balm-dialog v-model="isOpen" type="fit-content" theme="grey-10">
    <div v-if="entry" class="journal-detail-modal">
      <!-- Header: title + date -->
      <div class="detail-header">
        <h2 class="detail-title">
          {{ title }}
        </h2>
        <formatted-date
          v-if="entry.createdAt"
          :date="entry.createdAt"
          format="date"
          class="detail-date"
        />
      </div>

      <!-- Three-column layout -->
      <div class="detail-columns">
        <!-- Left: the mood, new vocabulary or legacy scale -->
        <div v-if="hasMood" class="detail-mood-column">
          <p class="column-label">
            {{ $t('journal_tile_mood_question') }}
          </p>
          <div class="mood-display">
            <mood-face :mood="entry.mood!" :size="40" tinted/>
            <span class="mood-label">{{ moodName }}</span>
          </div>
        </div>
        <div v-else-if="hasMoodLevel" class="detail-mood-column">
          <p class="column-label">
            {{ $t('select_mood_level') }}
          </p>
          <div class="mood-display">
            <img
              :src="getMoodLevelImage(entry.moodLevel!)"
              :alt="moodLevelLabel"
              class="mood-image"
            />
            <span class="mood-label">{{ moodLevelLabel }}</span>
          </div>
        </div>

        <!-- Center: Prompt + Answer. Dropped whole when the entry is a mood on its own. -->
        <div v-if="hasAnswer" class="detail-answer-column">
          <p v-if="(hasMood || hasMoodLevel) && entry.name" class="prompt-text">
            {{ entry.name }}
          </p>
          <div class="answer-text">
            {{ entry.answer }}
          </div>
        </div>

        <!-- Right: Intervention + Feelings -->
        <div v-if="hasSideColumn" class="detail-side-column">
          <div v-if="entry.interventionName" class="detail-intervention">
            <span class="intervention-label">{{ $t('part_of') }}</span>
            <badge-pill theme="light" :label="entry.interventionName"/>
          </div>
          <div v-if="hasFeelings" class="detail-feelings">
            <feeling-chip
              v-for="feeling in allFeelings"
              :key="feeling"
              :name="feeling"
            />
          </div>
        </div>
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.journal-detail-modal {
  display: flex;
  flex-direction: column;
  padding: 0 var(--Spacing-spacing-3) var(--Spacing-spacing-3);
  gap: var(--Spacing-spacing-7);

  @include screen-md {
    max-width: 960px;
  }

  @include screen-lg {
    min-width: 900px;
  }
}

.detail-header {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
  padding-top: var(--Spacing-spacing-5);
  padding-right: var(--Spacing-spacing-8);

  @include screen-lg {
    padding-top: var(--Spacing-spacing-3);
  }
}

.detail-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.detail-date {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
}

.detail-columns {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-7);

  @include screen-md {
    flex-direction: row;
    gap: var(--Spacing-spacing-9);
  }
}

.detail-mood-column {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);

  @include screen-md {
    flex: 0 0 140px;
  }
}

.column-label {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin: 0;
}

.mood-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-2);
  padding: var(--Spacing-spacing-4);
  border-radius: var(--card-border-radius);
  // A real surface, the same one the intervention block beside it uses, rather than a shadow on
  // nothing: a translucent black shadow is invisible against the dark scheme's background, so in
  // dark mode this card had no edges at all and the mood appeared to float in the modal.
  background: var(--Neutral-UI-neutral-ui-0);
}

.mood-image {
  width: 60px;
  height: 60px;
}

.mood-label {
  @include font-body-sm-bold;
  color: var(--Neutral-UI-neutral-ui-80);
  text-align: center;
}

.detail-answer-column {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);

  @include screen-md {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.prompt-text {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin: 0;
}

.answer-text {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-100);
  white-space: pre-line;
  overflow-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
}

.detail-side-column {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);

  @include screen-md {
    flex: 0 0 auto;
    max-width: 260px;
  }
}

.detail-intervention {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-4);
  background: var(--Neutral-UI-neutral-ui-0);
  border-radius: var(--card-border-radius);

  :deep(.current) {
    align-self: flex-start;
  }
}

.intervention-label {
  @include font-body-sm-bold;
  color: var(--Neutral-UI-neutral-ui-100);
}

.detail-feelings {
  display: flex;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-2);

  :deep(.feeling-chip) {
    background-color: var(--Neutral-UI-neutral-ui-20);
    color: var(--Neutral-UI-neutral-ui-90);
    @include font-body-sm-bold;
  }
}
</style>
